// ds-color-ignore-file: dev-only / internal tooling surface
"use client"

import { useEffect, useRef, useState } from "react"
import type { MessageIndex } from "@/scripts/build-message-index"
import type { Provenance, CmsProvenance } from "./provenance"
import type { ExperimentProvenance } from "./experiment-provenance"
import { cmsCartKey, type CartItem } from "./edit-cart"
import { useStringStatus, GateNote, ApprovalMark } from "./string-status-view"
import { ModalShell } from "./modal-shell"
import { cmsFieldLabel } from "@/lib/edit/editable-cms-fields"

/** Shared ⚗ badge — shown in both modals and the in-panel editor when the
 *  picked text is A/B-gated. */
export function ExperimentBadge({
  experiment,
}: {
  experiment?: ExperimentProvenance
}) {
  if (!experiment) return null
  return (
    <div
      style={{
        display: "inline-block",
        fontSize: 11,
        fontWeight: 700,
        color: "#7a4fd0",
        background: "#f1ecfb",
        borderRadius: 6,
        padding: "3px 8px",
        marginBottom: 10,
      }}
    >
      ⚗ {experiment.flag} · editing variant: {experiment.variant}
    </div>
  )
}

/**
 * Edit a string across all its locales. For an ambiguous provenance, the user
 * first picks which key produced the text (the false-positive rail — ambiguous
 * never auto-applies); for an exact hit it opens straight on the grid.
 */
export function LocaleGridModal({
  provenance,
  index,
  page,
  experiment,
  existing,
  focusLocale,
  idToken,
  onAddToCart,
  onClose,
}: {
  provenance: Provenance
  index: MessageIndex
  page: string
  experiment?: ExperimentProvenance
  /** When re-editing a staged cart item: seeds the staged values + preserves the original `old`. */
  existing?: CartItem
  /** Locale textarea to autofocus (the page's current locale). */
  focusLocale?: string
  /** SSO/dev idToken — sent as User-Authorization to the review status API.
   *  Optional: status (gate/approval) is only fetched when a token is present. */
  idToken?: string
  onAddToCart: (item: CartItem) => void
  onClose: () => void
}) {
  const [key, setKey] = useState<string | null>(
    provenance.status === "exact" ? provenance.key : null
  )
  const [draft, setDraft] = useState<Record<string, string>>({})
  const { status, error: statusError } = useStringStatus(key, idToken)
  const approvalByLocale = new Map(
    (status?.approvals ?? []).map((a) => [a.locale, a])
  )
  // Fresh sign-offs = the lock. A failed fetch leaves status null + error true,
  // so every locale reads as "unconfirmed" (fail-open — never a false unlocked).
  const approvedLocales = new Set(
    (status?.approvals ?? []).filter((a) => a.fresh).map((a) => a.locale)
  )
  const gridLocales = key ? Object.keys(index.byKey[key] ?? {}) : []
  const unconfirmedLocales = new Set(statusError ? gridLocales : [])
  const focusRef = useRef<HTMLTextAreaElement | null>(null)

  const locales = key ? (index.byKey[key] ?? {}) : {}

  useEffect(() => {
    if (!key) return
    // Seed from the originals, then overlay any staged edit so you refine your
    // own change rather than starting over.
    const staged =
      existing && existing.key === key
        ? Object.fromEntries(
            Object.entries(existing.values).map(([l, v]) => [l, v.new])
          )
        : {}
    setDraft({ ...(index.byKey[key] ?? {}), ...staged })
  }, [key, index, existing])

  // Autofocus the current-locale textarea, cursor at end, when the grid shows.
  useEffect(() => {
    const t = focusRef.current
    if (!t) return
    t.focus()
    t.setSelectionRange(t.value.length, t.value.length)
  }, [key])

  if (provenance.status === "miss") {
    return (
      <Shell onClose={onClose} title="Couldn’t locate this string">
        <p style={{ fontSize: 13, color: "#555" }}>
          No index match (dynamic or concatenated text). Key search is a later
          phase.
        </p>
      </Shell>
    )
  }

  if (!key && provenance.status === "ambiguous") {
    return (
      <Shell onClose={onClose} title="Which key produced this?">
        <p style={{ fontSize: 12, color: "#888", marginTop: -6 }}>
          ambiguous — confirm before editing
        </p>
        {provenance.candidates.map((k, i) => (
          <label
            key={k}
            style={{
              display: "flex",
              gap: 9,
              alignItems: "center",
              padding: "9px 11px",
              border: "1px solid #e4e4e8",
              borderRadius: 9,
              marginBottom: 7,
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            <input
              type="radio"
              name="ahd-dk"
              defaultChecked={i === 0}
              onChange={() => setKey(k)}
            />
            <code>{k}</code>
            {i === 0 ? (
              <span
                style={{ marginLeft: "auto", fontSize: 10, color: "#2b6cff" }}
              >
                on this page
              </span>
            ) : null}
          </label>
        ))}
        <button
          style={primaryBtn}
          onClick={() => setKey(provenance.candidates[0])}
        >
          Continue
        </button>
      </Shell>
    )
  }

  const k = key!
  const isTemplate = index.flags[k] === "template"

  return (
    <Shell onClose={onClose} title={k}>
      <div
        style={{ fontSize: 11, color: "#888", marginTop: -6, marginBottom: 10 }}
      >
        next-intl · edits → branch + PR · {page}
      </div>
      <GateNote tier={status?.tier ?? null} />
      <ExperimentBadge experiment={experiment} />
      {focusLocale && locales[focusLocale] !== undefined ? (
        <p
          style={{
            fontSize: 11,
            color: "#888",
            marginTop: 0,
            marginBottom: 10,
          }}
        >
          Editing all locales —{" "}
          <strong style={{ color: "#2b6cff" }}>
            {focusLocale.toUpperCase()}
          </strong>{" "}
          is what shows on this page.
        </p>
      ) : null}
      {isTemplate ? (
        <p style={{ color: "#b25e00", fontSize: 12 }}>
          Contains a {"{placeholder}"} — you are editing the raw template.
        </p>
      ) : null}
      {Object.keys(locales).map((loc) => {
        const isFocusLoc = focusLocale === loc
        // Flag locales whose source value is identical to the on-page locale,
        // so editing the wrong one (e.g. EN-NL when you meant EN) is obvious.
        const dup =
          !isFocusLoc &&
          focusLocale != null &&
          locales[focusLocale] !== undefined &&
          locales[loc] === locales[focusLocale]
        const isApproved = approvedLocales.has(loc)
        const isUnconfirmed = unconfirmedLocales.has(loc)
        // An approved row whose draft has drifted from the signed-off value will
        // re-enter the queue on save — surface that before they commit.
        const editedSinceApproval =
          isApproved && (draft[loc] ?? "") !== locales[loc]
        return (
          <div key={loc} style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", gap: 8 }}>
              <div style={{ width: 76, flexShrink: 0 }}>
                <strong
                  style={{
                    fontSize: 12,
                    color: isFocusLoc ? "#2b6cff" : "#666",
                    fontWeight: isFocusLoc ? 700 : 600,
                  }}
                >
                  {loc.toUpperCase()}
                </strong>
                {isApproved && approvalByLocale.has(loc) ? (
                  <ApprovalMark approval={approvalByLocale.get(loc)!} />
                ) : isUnconfirmed ? (
                  <div
                    style={{
                      fontSize: 11,
                      color: "#b25e00",
                      fontWeight: 700,
                      marginTop: 2,
                    }}
                    title="The approval check failed for this locale — try again before you rely on the lock state."
                  >
                    ⚠ approval unknown
                  </div>
                ) : null}
                {isFocusLoc ? (
                  <div
                    style={{
                      fontSize: 9,
                      color: "#2b6cff",
                      fontWeight: 700,
                      marginTop: 2,
                    }}
                  >
                    ● on this page
                  </div>
                ) : dup ? (
                  <div style={{ fontSize: 9, color: "#999", marginTop: 2 }}>
                    = same as {focusLocale!.toUpperCase()}
                  </div>
                ) : null}
              </div>
              <textarea
                ref={isFocusLoc ? focusRef : undefined}
                value={draft[loc] ?? ""}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, [loc]: e.target.value }))
                }
                style={{
                  flex: 1,
                  border: `1px solid ${
                    isFocusLoc ? "#2b6cff" : dup ? "#e7c200" : "#dcdce0"
                  }`,
                  borderRadius: 8,
                  padding: "8px 10px",
                  fontSize: 13,
                  minHeight: 34,
                  resize: "vertical",
                }}
              />
            </div>
            {editedSinceApproval ? (
              <p
                style={{
                  margin: "4px 0 0 84px",
                  fontSize: 11,
                  color: "#b25e00",
                }}
              >
                This string is approved — saving will send it back for
                re-approval.
              </p>
            ) : null}
          </div>
        )
      })}
      <button
        style={primaryBtn}
        onClick={() => {
          const values: CartItem["values"] = {}
          for (const loc of Object.keys(locales)) {
            if (draft[loc] !== locales[loc])
              values[loc] = {
                // Preserve the ORIGINAL old when refining a staged edit.
                old: existing?.values[loc]?.old ?? locales[loc],
                new: draft[loc],
              }
          }
          if (Object.keys(values).length)
            onAddToCart({ key: k, source: "intl", page, values, experiment })
          onClose()
        }}
      >
        Add to cart
      </button>
    </Shell>
  )
}

/**
 * Edit a single CMS (Payload) text field full-screen. Used for a `scalar` field
 * too long for the in-rail panel and for an `html` field (e.g. the long FAQ
 * answer) — the raw markup is the edit target, NOT the rendered DOM text.
 *
 * The complete raw field value is the source of truth, so the modal **fetches it
 * itself** (`/api/edit/cms-field`) rather than trusting a DOM-derived seed that
 * truncates a multi-paragraph `html` field to its first rendered block. The
 * fetched value is both the textarea seed (unless a staged edit is being
 * refined) and the `old` baseline the cart diff preserves. (AHD-7515)
 */
export function CmsEditModal({
  provenance,
  idToken,
  page,
  stagedValue,
  experiment,
  onAddToCart,
  onClose,
}: {
  provenance: CmsProvenance
  /** SSO token for the cms-field fetch (read the raw field value). */
  idToken: string
  page: string
  /** When re-editing a staged cart item: the staged new value to seed the
   *  textarea with instead of the fetched raw value. The baseline `old` still
   *  comes from the fetch, so the cart diff stays anchored to the live field. */
  stagedValue?: string
  experiment?: ExperimentProvenance
  onAddToCart: (item: CartItem) => void
  onClose: () => void
}) {
  const { collection, id, field, locale } = provenance
  const cartKey = cmsCartKey({ collection, id, field })
  const [value, setValue] = useState(stagedValue ?? "")
  // The complete raw field value, fetched on open — the `old` baseline the cart
  // diff preserves. Null until the fetch resolves.
  const [base, setBase] = useState<string | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const focusRef = useRef<HTMLTextAreaElement | null>(null)
  // Once the user types, the late-arriving fetch must not clobber their edit.
  const touchedRef = useRef(false)

  useEffect(() => {
    let cancelled = false
    const qs = new URLSearchParams({ collection, id, field, locale })
    fetch(`/api/edit/cms-field?${qs}`, {
      headers: { "User-Authorization": `Bearer ${idToken}` },
    })
      .then(async (res) => {
        if (!res.ok)
          throw new Error(`Couldn’t load this field (${res.status}).`)
        const { value: raw } = (await res.json()) as { value: unknown }
        if (cancelled) return
        const text = typeof raw === "string" ? raw : ""
        setBase(text)
        // Seed the textarea with the full raw value unless the user is refining a
        // staged edit (stagedValue) or has already started typing.
        if (stagedValue === undefined && !touchedRef.current) setValue(text)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setLoadError(
          err instanceof Error ? err.message : "Couldn’t load this field."
        )
      })
    return () => {
      cancelled = true
    }
  }, [collection, id, field, locale, idToken, stagedValue])

  const changed = base !== null && value !== base
  // Show the editor as soon as there's something to edit: a staged reopen has a
  // seed immediately; a fresh open waits for the fetched raw value. The Add
  // button stays disabled until `base` resolves (so `old` is the real value).
  const showEditor = stagedValue !== undefined || base !== null

  useEffect(() => {
    if (!showEditor) return
    const t = focusRef.current
    if (!t) return
    t.focus()
    t.setSelectionRange(t.value.length, t.value.length)
  }, [showEditor])

  const humanLabel = cmsFieldLabel(collection, field)
  const isHtml = provenance.kind === "html"

  return (
    <Shell
      onClose={onClose}
      ariaLabel={humanLabel}
      title={
        <div style={{ marginBottom: 10 }}>
          <h3 style={cmsHumanHeader}>{humanLabel}</h3>
          <code style={cmsIdSubline}>{cartKey}</code>
        </div>
      }
    >
      <div
        style={{
          display: "inline-block",
          fontSize: 11,
          fontWeight: 700,
          color: "#2f6f3e",
          background: "#e7f6ec",
          borderRadius: 6,
          padding: "3px 8px",
          marginTop: -6,
          marginBottom: 10,
        }}
      >
        Saves as a draft — needs publishing.
      </div>
      {isHtml ? (
        <div style={htmlHint} title="This field stores raw HTML markup.">
          HTML · this field accepts HTML
        </div>
      ) : null}
      <ExperimentBadge experiment={experiment} />
      {loadError ? (
        <p style={{ color: "#b3261e", fontSize: 13 }}>{loadError}</p>
      ) : !showEditor ? (
        <p style={{ color: "#888", fontSize: 13, padding: "12px 0" }}>
          Loading the full field…
        </p>
      ) : (
        <>
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <span
              style={{
                width: 76,
                flexShrink: 0,
                fontSize: 11,
                color: "#6b7280",
                fontWeight: 600,
              }}
            >
              Locale: {locale.toUpperCase()}
            </span>
            <textarea
              ref={focusRef}
              value={value}
              onChange={(e) => {
                touchedRef.current = true
                setValue(e.target.value)
              }}
              style={{
                flex: 1,
                border: "1px solid #2b6cff",
                borderRadius: 8,
                padding: "8px 10px",
                fontSize: 13,
                minHeight: 320,
                resize: "vertical",
                fontFamily: "ui-monospace, monospace",
                lineHeight: 1.5,
              }}
            />
          </div>
          <button
            style={{
              ...primaryBtn,
              background: changed ? "#2f6f3e" : "#e7e7ea",
              color: changed ? "#fff" : "#9a9aa2",
              cursor: changed ? "pointer" : "default",
            }}
            disabled={!changed}
            onClick={() => {
              if (changed)
                onAddToCart({
                  key: cartKey,
                  source: "cms",
                  page,
                  values: { [locale]: { old: base, new: value } },
                  cms: { collection, id, field },
                  experiment,
                })
              onClose()
            }}
          >
            Add to cart
          </button>
        </>
      )}
    </Shell>
  )
}

const primaryBtn: React.CSSProperties = {
  border: "none",
  borderRadius: 9,
  padding: "10px 16px",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  background: "#0b0b0f",
  color: "#fff",
}
const cmsHumanHeader: React.CSSProperties = {
  margin: 0,
  fontSize: 16,
  fontWeight: 600,
  color: "#15151a",
  fontFamily: "ui-sans-serif, system-ui",
}
const cmsIdSubline: React.CSSProperties = {
  display: "block",
  marginTop: 3,
  fontSize: 11,
  color: "#9a9aa2",
  fontFamily: "ui-monospace, monospace",
  wordBreak: "break-all",
}
const htmlHint: React.CSSProperties = {
  display: "inline-block",
  fontSize: 11,
  fontWeight: 700,
  color: "#5a4a22",
  background: "#fdf6e9",
  border: "1px solid #f0d8a8",
  borderRadius: 6,
  padding: "3px 8px",
  marginBottom: 10,
  marginRight: 6,
  fontFamily: "ui-monospace, monospace",
}

function Shell({
  children,
  onClose,
  title,
  ariaLabel,
}: {
  children: React.ReactNode
  onClose: () => void
  title?: React.ReactNode
  ariaLabel?: string
}) {
  return (
    <ModalShell
      onClose={onClose}
      title={title}
      ariaLabel={ariaLabel}
      width={560}
      maxHeight="82vh"
    >
      {children}
    </ModalShell>
  )
}
